import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  CadixProgressPhase,
  CADIX_THINKING_MESSAGE,
  CadixStream,
  CadixStreamAuthError,
  CadixStreamClient,
  CadixStreamEvent,
  CadixStreamHttpError,
  CadixStreamTerminalError,
} from './cadix-stream.client';
import { CadixRowFocusService } from './cadix-row-focus.service';
import { CadixWorkspaceStateService } from './cadix-workspace-state.service';

export { CADIX_THINKING_MESSAGE } from './cadix-stream.client';

export interface ChatMessage {
  role: 'user' | 'assistant';
  /** Raw message text. Completed assistant messages are rendered as Markdown by the view. */
  content?: string;
  /** Legacy formatted representation retained for compatibility with existing callers/tests. */
  html: string;
  /** Marks an assistant error message so the UI can offer a retry action. */
  error?: boolean;
  /** The question that failed, retained so the request can be retried. */
  question?: string;
}

interface ConversationMessageResponse {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

interface ConversationResponse {
  conversationId: string;
  scope: 'general' | 'project';
  projectId: string | null;
  activeJobId: string | null;
  createdAt: string;
  expiresAt: string;
  messages: ConversationMessageResponse[];
}

interface AgentTurnResponse {
  conversationId: string;
  reply: string;
  activeJobId: string | null;
  messages: ConversationMessageResponse[];
}

interface CachedMessage {
  role: 'user' | 'assistant';
  /** Escaped source text. Display HTML is regenerated after every restore. */
  content: string;
  error?: boolean;
  question?: string;
}

interface ContextState {
  conversationId: string | null;
  activeJobId: string | null;
  draft: string;
  cache: CachedMessage[];
  authoritativeMessageCount: number;
  lastAuthoritativeCreatedAt: string | null;
}

interface TurnBaseline {
  messageCount: number;
  activeJobId: string | null;
  lastAuthoritativeCreatedAt: string | null;
}

interface StoredContext {
  conversationId: string | null;
  activeJobId: string | null;
  draft: string;
  messages: CachedMessage[];
}

interface StoredCadixState {
  version: 2;
  contexts: Record<string, StoredContext>;
}

interface ConversationContext {
  key: string;
  projectId: string | null;
}

export const CADIX_STORAGE_KEY = 'cadix-chat-v2';
const LEGACY_STORAGE_KEY = 'cadix-chat-messages';
const GENERAL_CONTEXT: ConversationContext = { key: 'general', projectId: null };
const ERROR_TEXT = 'Sorry, I could not reach the assistant. Please try again.';
const GREETING_TEXT =
  "Hello! I'm **CADIX**, your provider validation assistant. " +
  'I help verify provider identities, identify missing or conflicting information, and explain the evidence behind validation results.';
const DELTA_MAX_WAIT_MS = 100;
const PROGRESS_TEXT: Record<CadixProgressPhase, string> = {
  thinking: CADIX_THINKING_MESSAGE,
  retrieving: 'CADIX is checking authorized data.',
  composing: 'CADIX is preparing the response.',
};

/**
 * App-wide CADIX state, partitioned into one general conversation and one
 * independent server conversation for every project route.
 */
@Injectable({ providedIn: 'root' })
export class CadixChatService {
  private readonly http = inject(HttpClient);
  private readonly streamClient = inject(CadixStreamClient);
  private readonly router = inject(Router, { optional: true });
  private readonly rowFocus = inject(CadixRowFocusService);
  private readonly workspace = inject(CadixWorkspaceStateService);
  private readonly contexts = this.restore();
  private readonly activeContext = signal<ConversationContext>(
    this.contextFromUrl(this.router?.url ?? '/home'),
  );

  readonly messages = signal<ChatMessage[]>(this.renderActiveCache());
  readonly draft = signal(this.activeState().draft);
  readonly loading = signal(false);
  /** @deprecated Prefer CadixWorkspaceStateService for layout state. */
  readonly open = this.workspace.open;
  readonly phase = signal<CadixProgressPhase | null>(null);
  readonly partialText = signal('');
  readonly progressLabel = computed(() => {
    const phase = this.phase();
    return phase === null ? '' : PROGRESS_TEXT[phase];
  });
  readonly liveAnnouncement = signal('');
  readonly isProjectContext = computed(() => this.activeContext().projectId !== null);
  readonly contextLabel = signal(
    this.activeContext().projectId === null ? 'General' : 'Current file',
  );
  readonly activeProjectId = computed(() => this.activeContext().projectId);
  readonly activeJobId = computed(() => {
    this.messages();
    return this.activeState().activeJobId;
  });
  readonly hasUserMessages = computed(() =>
    this.messages().some((message) => message.role === 'user'),
  );

  private generation = 0;
  private activeRequest?: Subscription;
  private activeStreamAbort?: AbortController;
  private persistenceEnabled = true;
  private pendingDelta = '';
  private deltaAnimationFrame: number | null = null;
  private deltaMaxWait: ReturnType<typeof setTimeout> | null = null;
  private readonly announcedPhases = new Set<CadixProgressPhase>();
  private terminalAnnounced = false;
  private activeTurnRowFocusIds: readonly string[] = [];

  constructor() {
    effect(() => {
      const state = this.activeState();
      state.draft = this.draft();
      if (this.persistenceEnabled) {
        this.persist();
      }
    });

    if (this.isHomeUrl(this.router?.url ?? '/home')) {
      this.workspace.closePanel();
    }
    this.activate(this.activeContext());
    this.router?.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        if (this.isHomeUrl(event.urlAfterRedirects)) {
          this.workspace.closePanel();
        }
        this.activate(this.contextFromUrl(event.urlAfterRedirects));
      });
  }

  openPanel(): void {
    this.workspace.openPanel();
  }

  closePanel(): void {
    this.workspace.closePanel();
  }

  toggle(): void {
    this.workspace.toggle();
  }

  setContextLabel(label: string | null): void {
    this.contextLabel.set(label?.trim() || (this.isProjectContext() ? 'Current file' : 'General'));
  }

  /** Clear only the active context and best-effort delete its server conversation. */
  reset(): void {
    const context = this.activeContext();
    const state = this.activeState();
    const conversationId = state.conversationId;
    this.cancelActiveRequest();
    this.clearState(state);
    this.publish(state);

    if (conversationId) {
      this.http.delete(this.conversationUrl(context, conversationId)).subscribe({
        error: () => undefined,
      });
    }
  }

  send(text?: string): void {
    const message = (text ?? this.draft()).trim();
    if (!message || this.loading()) {
      return;
    }

    this.persistenceEnabled = true;
    const state = this.activeState();
    state.cache.push(this.toCache('user', message));
    state.draft = '';
    this.publish(state);
    this.request(message);
  }

  /** Stop the active request while preserving any streamed text already shown. */
  stop(): void {
    if (!this.loading()) {
      return;
    }
    this.generation++;
    this.activeRequest?.unsubscribe();
    this.activeRequest = undefined;
    this.activeStreamAbort?.abort();
    this.activeStreamAbort = undefined;
    this.activeTurnRowFocusIds = [];
    this.stopStreamingButKeepPartial();
    this.loading.set(false);
    this.liveAnnouncement.set('CADIX stopped generating.');
  }

  /** Retry the failed request represented by an error message. */
  retry(index: number): void {
    const messages = this.messages();
    const target = messages[index];
    if (!target?.error || this.loading()) {
      return;
    }

    const state = this.activeState();
    const cached = state.cache[index];
    const question =
      (cached?.question ? this.decodeEscaped(cached.question) : null) ??
      (() => {
        const priorUser = state.cache
          .slice(0, index)
          .reverse()
          .find((message) => message.role === 'user');
        return priorUser ? this.decodeEscaped(priorUser.content) : null;
      })();
    if (!question) {
      return;
    }

    const wasLast = index === state.cache.length - 1;
    state.cache = state.cache.filter((_, messageIndex) => messageIndex !== index);
    if (!wasLast) {
      state.cache.push(this.toCache('user', question));
    }
    this.publish(state);
    this.request(question);
  }

  /**
   * Clear CADIX's session-only state before Okta tears down authentication.
   * Other application and Okta session keys are intentionally untouched.
   */
  clearSessionStorage(): void {
    this.cancelActiveRequest();
    this.workspace.clearSessionStorage();
    this.persistenceEnabled = false;
    this.contexts.clear();
    const state = this.newState();
    this.contexts.set(this.activeContext().key, state);
    this.messages.set(state.cache.map((message) => this.render(message)));
    this.draft.set('');
    try {
      sessionStorage.removeItem(CADIX_STORAGE_KEY);
      sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Ignore inaccessible storage; in-memory state has still been cleared.
    }
  }

  private request(message: string): void {
    const context = this.activeContext();
    const state = this.activeState();
    const generation = this.beginRequest();
    const baseline = this.snapshotBaseline(state);
    this.activeTurnRowFocusIds = this.isProjectContext()
      ? [...this.rowFocus.requestRowIds()]
      : [];
    this.loading.set(true);
    this.updatePhase('thinking');

    if (state.conversationId) {
      void this.postStream(context, state, message, generation, baseline);
    } else {
      this.createAndPost(context, state, message, generation, false);
    }
  }

  private createAndPost(
    context: ConversationContext,
    state: ContextState,
    message: string,
    generation: number,
    recreated: boolean,
  ): void {
    this.activeRequest = this.http
      .post<unknown>(this.conversationsUrl(context), {})
      .subscribe({
        next: (value) => {
          if (!this.isCurrent(context, state, generation)) {
            return;
          }
          const response = this.parseConversation(value, context);
          if (!response || this.isExpired(response.expiresAt)) {
            state.conversationId = null;
            state.activeJobId = null;
            this.handleRequestError(context, state, message, generation);
            return;
          }
          state.conversationId = response.conversationId;
          this.updateAuthoritativeState(state, response.activeJobId, response.messages);
          this.persist();
          if (recreated) {
            this.postAtomic(context, state, message, generation, true);
          } else {
            void this.postStream(
              context,
              state,
              message,
              generation,
              this.snapshotBaseline(state),
            );
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.clearSessionStorage();
            return;
          }
          this.handleRequestError(context, state, message, generation);
        },
      });
  }

  private async postStream(
    context: ConversationContext,
    state: ContextState,
    message: string,
    generation: number,
    baseline: TurnBaseline,
  ): Promise<void> {
    const conversationId = state.conversationId;
    if (!conversationId || !this.isCurrent(context, state, generation)) {
      return;
    }

    const controller = new AbortController();
    this.activeStreamAbort = controller;
    const turnId = crypto.randomUUID();

    try {
      const stream = await this.streamClient.open(
        this.conversationUrl(context, conversationId),
        message,
        turnId,
        controller.signal,
        this.activeTurnRowFocusIds,
      );
      if (!stream.responseAccepted || !this.isCurrent(context, state, generation)) {
        return;
      }
      await this.consumeStream(stream, context, state, message, conversationId, generation);
    } catch (error) {
      this.handleStreamFailure(
        error,
        context,
        state,
        message,
        generation,
        baseline,
      );
    } finally {
      if (this.activeStreamAbort === controller) {
        this.activeStreamAbort = undefined;
      }
    }
  }

  private async consumeStream(
    stream: CadixStream,
    context: ConversationContext,
    state: ContextState,
    message: string,
    conversationId: string,
    generation: number,
  ): Promise<void> {
    for await (const event of stream.events) {
      if (!this.isCurrent(context, state, generation)) {
        return;
      }
      switch (event.type) {
        case 'accepted':
          break;
        case 'progress':
          this.updatePhase(event.phase);
          break;
        case 'delta':
          this.queueDelta(event.text, context, state, generation);
          break;
        case 'done':
          if (!this.applyAuthoritativeDone(state, message, conversationId, event)) {
            throw new Error('Invalid authoritative CADIX response');
          }
          this.announceTerminal(event.reply);
          this.finishRequest();
          return;
        case 'error':
          throw new CadixStreamTerminalError(event.code, event.message);
      }
    }
    throw new Error('CADIX stream ended without a terminal event');
  }

  private handleStreamFailure(
    error: unknown,
    context: ConversationContext,
    state: ContextState,
    message: string,
    generation: number,
    baseline: TurnBaseline,
  ): void {
    if (!this.isCurrent(context, state, generation) || this.isAbortError(error)) {
      return;
    }
    if (
      error instanceof CadixStreamHttpError &&
      (error.status === 404 || error.status === 405 || error.status === 501)
    ) {
      this.postAtomic(context, state, message, generation, false);
      return;
    }
    if (
      error instanceof CadixStreamAuthError ||
      (error instanceof CadixStreamHttpError && error.status === 401)
    ) {
      this.clearSessionStorage();
      return;
    }
    if (error instanceof CadixStreamHttpError) {
      if (error.status === 403 || error.status === 404) {
        state.conversationId = null;
        state.activeJobId = null;
        state.authoritativeMessageCount = 0;
        state.lastAuthoritativeCreatedAt = null;
      }
      this.handleRequestError(context, state, message, generation);
      return;
    }

    // No response or any failure after HTTP 200 is delivery-ambiguous.
    this.reconcileOnce(
      context,
      state,
      message,
      generation,
      baseline,
      error instanceof CadixStreamTerminalError ? error.message : ERROR_TEXT,
    );
  }

  private postAtomic(
    context: ConversationContext,
    state: ContextState,
    message: string,
    generation: number,
    recreated: boolean,
  ): void {
    const conversationId = state.conversationId;
    if (!conversationId || !this.isCurrent(context, state, generation)) {
      return;
    }

    this.activeRequest = this.http
      .post<unknown>(`${this.conversationUrl(context, conversationId)}/messages`, {
        message,
        ...(this.activeTurnRowFocusIds.length
          ? { rowFocusIds: this.activeTurnRowFocusIds }
          : {}),
      })
      .subscribe({
        next: (value) => {
          if (!this.isCurrent(context, state, generation)) {
            return;
          }
          const response = this.parseTurn(value);
          if (response?.conversationId !== conversationId) {
            this.handleRequestError(context, state, message, generation);
            return;
          }

          // Every turn carries authoritative history. This also guarantees
          // that a server-side rerun reset replaces all old-job display cache.
          state.cache = response.messages.map((item) => this.toCache(item.role, item.content));
          this.updateAuthoritativeState(state, response.activeJobId, response.messages);
          this.publish(state);
          this.announceTerminal(response.reply);
          this.finishRequest();
        },
        error: (error: HttpErrorResponse) => {
          if (!this.isCurrent(context, state, generation)) {
            return;
          }
          if (error.status === 401) {
            this.clearSessionStorage();
            return;
          }
          if (!recreated && (error.status === 403 || error.status === 404)) {
            state.conversationId = null;
            state.activeJobId = null;
            state.cache = [this.greetingCache(), this.toCache('user', message)];
            state.authoritativeMessageCount = 1;
            state.lastAuthoritativeCreatedAt = null;
            this.publish(state);
            this.createAndPost(
              context,
              state,
              message,
              generation,
              true,
            );
            return;
          }
          if (error.status === 403 || error.status === 404) {
            state.conversationId = null;
            state.activeJobId = null;
            state.authoritativeMessageCount = 0;
            state.lastAuthoritativeCreatedAt = null;
          }
          this.handleRequestError(context, state, message, generation);
        },
      });
  }

  private reconcileOnce(
    context: ConversationContext,
    state: ContextState,
    message: string,
    generation: number,
    baseline: TurnBaseline,
    fallbackErrorText: string,
  ): void {
    const conversationId = state.conversationId;
    if (!conversationId || !this.isCurrent(context, state, generation)) {
      return;
    }
    this.stopStreamingButKeepPartial();
    this.activeRequest = this.http
      .get<unknown>(this.conversationUrl(context, conversationId))
      .subscribe({
        next: (value) => {
          if (!this.isCurrent(context, state, generation)) {
            return;
          }
          const response = this.parseConversation(value, context);
          if (
            response?.conversationId !== conversationId ||
            !this.hasRecoveredTail(response, message, baseline)
          ) {
            this.handleRequestError(
              context,
              state,
              message,
              generation,
              fallbackErrorText,
              true,
            );
            return;
          }
          state.cache = response.messages.map((item) => this.toCache(item.role, item.content));
          this.updateAuthoritativeState(state, response.activeJobId, response.messages);
          this.publish(state);
          this.announceTerminal(response.messages.at(-1)?.content ?? '');
          this.finishRequest();
        },
        error: (error: HttpErrorResponse) => {
          if (!this.isCurrent(context, state, generation)) {
            return;
          }
          if (error.status === 401) {
            this.clearSessionStorage();
            return;
          }
          if (error.status === 403 || error.status === 404) {
            state.conversationId = null;
            state.activeJobId = null;
            state.authoritativeMessageCount = 0;
            state.lastAuthoritativeCreatedAt = null;
          }
          this.handleRequestError(
            context,
            state,
            message,
            generation,
            fallbackErrorText,
            true,
          );
        },
      });
  }

  private applyAuthoritativeDone(
    state: ContextState,
    message: string,
    conversationId: string,
    event: Extract<CadixStreamEvent, { type: 'done' }>,
  ): boolean {
    const tail = event.messages.slice(-2);
    if (
      event.conversationId !== conversationId ||
      tail.length !== 2 ||
      tail[0].role !== 'user' ||
      tail[0].content !== message ||
      tail[1].role !== 'assistant' ||
      tail[1].content !== event.reply
    ) {
      return false;
    }
    state.cache = event.messages.map((item) => this.toCache(item.role, item.content));
    this.updateAuthoritativeState(state, event.activeJobId, event.messages);
    this.publish(state);
    return true;
  }

  private hasRecoveredTail(
    response: ConversationResponse,
    message: string,
    baseline: TurnBaseline,
  ): boolean {
    const tail = response.messages.slice(-2);
    const exactNewTail =
      tail[0]?.role === 'user' &&
      tail[0].content === message &&
      tail[1]?.role === 'assistant' &&
      tail[1].createdAt !== baseline.lastAuthoritativeCreatedAt;
    if (!exactNewTail) {
      return false;
    }

    return (
      response.messages.length >= baseline.messageCount + 2 ||
      response.activeJobId !== baseline.activeJobId ||
      response.messages.length < baseline.messageCount ||
      (baseline.messageCount >= 23 && response.messages.length === 24)
    );
  }

  private snapshotBaseline(state: ContextState): TurnBaseline {
    return {
      messageCount: state.authoritativeMessageCount,
      activeJobId: state.activeJobId,
      lastAuthoritativeCreatedAt: state.lastAuthoritativeCreatedAt,
    };
  }

  private updateAuthoritativeState(
    state: ContextState,
    activeJobId: string | null,
    messages: ConversationMessageResponse[],
  ): void {
    state.activeJobId = activeJobId;
    state.authoritativeMessageCount = messages.length;
    state.lastAuthoritativeCreatedAt = messages.at(-1)?.createdAt ?? null;
  }

  private finishRequest(): void {
    this.clearTransient();
    this.activeTurnRowFocusIds = [];
    this.loading.set(false);
  }

  private clearTransient(clearAnnouncement = false): void {
    this.cancelDeltaFlush();
    this.phase.set(null);
    this.partialText.set('');
    if (clearAnnouncement) {
      this.announcedPhases.clear();
      this.terminalAnnounced = false;
      this.liveAnnouncement.set('');
    }
  }

  private handleRequestError(
    context: ConversationContext,
    state: ContextState,
    message: string,
    generation: number,
    errorText = ERROR_TEXT,
    preservePartial = false,
  ): void {
    if (!this.isCurrent(context, state, generation)) {
      return;
    }
    state.cache.push(this.toCache('assistant', errorText, true, message));
    if (preservePartial) {
      this.stopStreamingButKeepPartial();
    } else {
      this.clearTransient();
    }
    this.publish(state);
    this.announceTerminal(errorText);
    this.loading.set(false);
  }

  private activate(context: ConversationContext): void {
    if (context.key === this.activeContext().key && this.loading()) {
      return;
    }

    // Capture a draft synchronously in case navigation occurs before the
    // persistence effect scheduled by the latest input event has run.
    this.activeState().draft = this.draft();
    this.cancelActiveRequest();
    this.activeContext.set(context);
    this.contextLabel.set(context.projectId === null ? 'General' : 'Current file');
    const state = this.stateFor(context.key);
    this.publish(state);

    if (!state.conversationId) {
      return;
    }

    const generation = this.beginRequest();
    this.loading.set(true);
    this.activeRequest = this.http
      .get<unknown>(this.conversationUrl(context, state.conversationId))
      .subscribe({
        next: (value) => {
          if (!this.isCurrent(context, state, generation)) {
            return;
          }
          const response = this.parseConversation(value, context);
          if (!response || this.isExpired(response.expiresAt)) {
            this.clearState(state);
          } else {
            state.conversationId = response.conversationId;
            state.cache = response.messages.map((item) => this.toCache(item.role, item.content));
            this.updateAuthoritativeState(state, response.activeJobId, response.messages);
          }
          this.publish(state);
          this.loading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          if (!this.isCurrent(context, state, generation)) {
            return;
          }
          if (error.status === 401) {
            this.clearSessionStorage();
            return;
          }
          if (error.status === 403 || error.status === 404) {
            this.clearState(state);
            this.publish(state);
          }
          this.loading.set(false);
        },
      });
  }

  private contextFromUrl(url: string): ConversationContext {
    const path = url.split('?')[0].split('#')[0];
    const match = /^\/files\/([^/]+)\/(?:edit|review\/table)\/?$/.exec(path);
    if (!match) {
      return GENERAL_CONTEXT;
    }
    try {
      const projectId = decodeURIComponent(match[1]);
      return { key: `project:${projectId}`, projectId };
    } catch {
      return GENERAL_CONTEXT;
    }
  }

  private isHomeUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/' || /^\/home\/?$/.test(path);
  }

  private conversationsUrl(context: ConversationContext): string {
    return context.projectId === null
      ? '/api/cadix/conversations'
      : `/api/projects/${encodeURIComponent(context.projectId)}/cadix/conversations`;
  }

  private conversationUrl(context: ConversationContext, conversationId: string): string {
    return `${this.conversationsUrl(context)}/${encodeURIComponent(conversationId)}`;
  }

  private beginRequest(): number {
    this.activeRequest?.unsubscribe();
    this.activeRequest = undefined;
    this.activeStreamAbort?.abort();
    this.activeStreamAbort = undefined;
    this.clearTransient(true);
    return ++this.generation;
  }

  private cancelActiveRequest(): void {
    this.generation++;
    this.activeRequest?.unsubscribe();
    this.activeRequest = undefined;
    this.activeStreamAbort?.abort();
    this.activeStreamAbort = undefined;
    this.activeTurnRowFocusIds = [];
    this.clearTransient(true);
    this.loading.set(false);
  }

  private updatePhase(phase: CadixProgressPhase): void {
    this.phase.set(phase);
    if (this.announcedPhases.has(phase) || this.terminalAnnounced) {
      return;
    }
    this.announcedPhases.add(phase);
    this.liveAnnouncement.set(PROGRESS_TEXT[phase]);
  }

  private queueDelta(
    text: string,
    context: ConversationContext,
    state: ContextState,
    generation: number,
  ): void {
    this.pendingDelta += text;
    if (this.deltaAnimationFrame !== null || this.deltaMaxWait !== null) {
      return;
    }

    const flush = () => {
      this.cancelScheduledDeltaCallbacks();
      if (!this.isCurrent(context, state, generation)) {
        this.pendingDelta = '';
        return;
      }
      const delta = this.pendingDelta;
      this.pendingDelta = '';
      if (delta) {
        this.partialText.update((partial) => partial + delta);
      }
    };

    if (typeof globalThis.requestAnimationFrame === 'function') {
      this.deltaAnimationFrame = globalThis.requestAnimationFrame(flush);
    }
    this.deltaMaxWait = setTimeout(flush, DELTA_MAX_WAIT_MS);
  }

  private cancelDeltaFlush(): void {
    this.cancelScheduledDeltaCallbacks();
    this.pendingDelta = '';
  }

  private stopStreamingButKeepPartial(): void {
    this.cancelScheduledDeltaCallbacks();
    const pending = this.pendingDelta;
    this.pendingDelta = '';
    if (pending) {
      this.partialText.update((partial) => partial + pending);
    }
    this.phase.set(null);
  }

  private cancelScheduledDeltaCallbacks(): void {
    if (
      this.deltaAnimationFrame !== null &&
      typeof globalThis.cancelAnimationFrame === 'function'
    ) {
      globalThis.cancelAnimationFrame(this.deltaAnimationFrame);
    }
    if (this.deltaMaxWait !== null) {
      clearTimeout(this.deltaMaxWait);
    }
    this.deltaAnimationFrame = null;
    this.deltaMaxWait = null;
  }

  private announceTerminal(text: string): void {
    if (this.terminalAnnounced) {
      return;
    }
    this.terminalAnnounced = true;
    this.liveAnnouncement.set(this.toPlainText(text));
  }

  private isCurrent(
    context: ConversationContext,
    state: ContextState,
    generation: number,
  ): boolean {
    return (
      generation === this.generation &&
      context.key === this.activeContext().key &&
      state === this.activeState()
    );
  }

  private activeState(): ContextState {
    return this.stateFor(this.activeContext().key);
  }

  private stateFor(key: string): ContextState {
    let state = this.contexts.get(key);
    if (!state) {
      state = this.newState();
      this.contexts.set(key, state);
    }
    return state;
  }

  private newState(): ContextState {
    return {
      conversationId: null,
      activeJobId: null,
      draft: '',
      cache: [this.greetingCache()],
      authoritativeMessageCount: 1,
      lastAuthoritativeCreatedAt: null,
    };
  }

  private clearState(state: ContextState): void {
    state.conversationId = null;
    state.activeJobId = null;
    state.draft = '';
    state.cache = [this.greetingCache()];
    state.authoritativeMessageCount = 1;
    state.lastAuthoritativeCreatedAt = null;
  }

  private publish(state: ContextState): void {
    this.messages.set(state.cache.map((message) => this.render(message)));
    this.draft.set(state.draft);
    this.persist();
  }

  private renderActiveCache(): ChatMessage[] {
    return this.activeState().cache.map((message) => this.render(message));
  }

  private greetingCache(): CachedMessage {
    return this.toCache('assistant', GREETING_TEXT);
  }

  private toCache(
    role: 'user' | 'assistant',
    text: string,
    error?: boolean,
    question?: string,
  ): CachedMessage {
    return {
      role,
      content: this.escape(text),
      ...(error ? { error: true } : {}),
      ...(question ? { question: this.escape(question) } : {}),
    };
  }

  private render(message: CachedMessage): ChatMessage {
    const text = this.decodeEscaped(message.content);
    return {
      role: message.role,
      content: text,
      html: message.role === 'assistant' ? this.format(text) : this.escape(text),
      ...(message.error ? { error: true } : {}),
      ...(message.question ? { question: this.decodeEscaped(message.question) } : {}),
    };
  }

  private parseConversation(
    value: unknown,
    context: ConversationContext,
  ): ConversationResponse | null {
    const candidate = value as Partial<ConversationResponse> | null;
    if (
      !candidate ||
      typeof candidate !== 'object' ||
      typeof candidate.conversationId !== 'string' ||
      (candidate.scope !== 'general' && candidate.scope !== 'project') ||
      !(candidate.projectId === null || typeof candidate.projectId === 'string') ||
      !(candidate.activeJobId === null || typeof candidate.activeJobId === 'string') ||
      typeof candidate.createdAt !== 'string' ||
      typeof candidate.expiresAt !== 'string' ||
      !this.isResponseMessages(candidate.messages)
    ) {
      return null;
    }
    const expectedScope = context.projectId === null ? 'general' : 'project';
    if (candidate.scope !== expectedScope || candidate.projectId !== context.projectId) {
      return null;
    }
    return candidate as ConversationResponse;
  }

  private parseTurn(value: unknown): AgentTurnResponse | null {
    const candidate = value as Partial<AgentTurnResponse> | null;
    if (
      !candidate ||
      typeof candidate !== 'object' ||
      typeof candidate.conversationId !== 'string' ||
      typeof candidate.reply !== 'string' ||
      !(candidate.activeJobId === null || typeof candidate.activeJobId === 'string') ||
      !this.isResponseMessages(candidate.messages)
    ) {
      return null;
    }
    return candidate as AgentTurnResponse;
  }

  private isResponseMessages(value: unknown): value is ConversationMessageResponse[] {
    return (
      Array.isArray(value) &&
      value.every((item) => {
        const candidate = item as Partial<ConversationMessageResponse> | null;
        return (
          !!candidate &&
          typeof candidate === 'object' &&
          (candidate.role === 'user' || candidate.role === 'assistant') &&
          typeof candidate.content === 'string' &&
          typeof candidate.createdAt === 'string'
        );
      })
    );
  }

  private isExpired(expiresAt: string): boolean {
    const timestamp = Date.parse(expiresAt);
    return !Number.isFinite(timestamp) || timestamp <= Date.now();
  }

  private isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
  }

  private restore(): Map<string, ContextState> {
    const contexts = new Map<string, ContextState>();
    try {
      const parsed = JSON.parse(
        sessionStorage.getItem(CADIX_STORAGE_KEY) ?? 'null',
      ) as Partial<StoredCadixState> | null;
      if (
        parsed?.version !== 2 ||
        !parsed.contexts ||
        typeof parsed.contexts !== 'object' ||
        Array.isArray(parsed.contexts)
      ) {
        return contexts;
      }
      for (const [key, value] of Object.entries(parsed.contexts)) {
        if (!this.isContextKey(key) || !this.isStoredContext(value)) {
          continue;
        }
        contexts.set(key, {
          conversationId: value.conversationId,
          activeJobId: value.activeJobId,
          draft: value.draft,
          cache: value.messages.map((message) => this.normalizeCached(message)),
          authoritativeMessageCount: value.messages.filter((message) => !message.error).length,
          lastAuthoritativeCreatedAt: null,
        });
      }
    } catch {
      // Ignore malformed or inaccessible storage and start with a greeting.
    }
    return contexts;
  }

  private isContextKey(key: string): boolean {
    return key === 'general' || (key.startsWith('project:') && key.length > 8);
  }

  private isStoredContext(value: unknown): value is StoredContext {
    const candidate = value as Partial<StoredContext> | null;
    return (
      !!candidate &&
      typeof candidate === 'object' &&
      (candidate.conversationId === null ||
        (typeof candidate.conversationId === 'string' &&
          candidate.conversationId.length > 0)) &&
      (candidate.activeJobId === null || typeof candidate.activeJobId === 'string') &&
      typeof candidate.draft === 'string' &&
      Array.isArray(candidate.messages) &&
      candidate.messages.length > 0 &&
      candidate.messages.every((message) => this.isCachedMessage(message))
    );
  }

  private isCachedMessage(value: unknown): value is CachedMessage {
    const candidate = value as Partial<CachedMessage> | null;
    return (
      !!candidate &&
      typeof candidate === 'object' &&
      (candidate.role === 'user' || candidate.role === 'assistant') &&
      typeof candidate.content === 'string' &&
      (candidate.error === undefined || typeof candidate.error === 'boolean') &&
      (candidate.question === undefined || typeof candidate.question === 'string')
    );
  }

  private normalizeCached(message: CachedMessage): CachedMessage {
    return {
      role: message.role,
      content: this.escape(this.decodeEscaped(message.content)),
      ...(message.error ? { error: true } : {}),
      ...(message.question
        ? { question: this.escape(this.decodeEscaped(message.question)) }
        : {}),
    };
  }

  private persist(): void {
    if (!this.persistenceEnabled) {
      return;
    }
    const stored: StoredCadixState = { version: 2, contexts: {} };
    for (const [key, state] of this.contexts) {
      stored.contexts[key] = {
        conversationId: state.conversationId,
        activeJobId: state.activeJobId,
        draft: state.draft,
        messages: state.cache,
      };
    }
    try {
      sessionStorage.setItem(CADIX_STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Ignore storage failures (for example, private mode or quota limits).
    }
  }

  private decodeEscaped(text: string): string {
    return text.replaceAll('&lt;', '<').replaceAll('&gt;', '>').replaceAll('&amp;', '&');
  }

  private toPlainText(text: string): string {
    const withoutHtml = this.stripHtml(this.decodeHtmlEntities(text));
    return this.stripMarkdownLinks(withoutHtml)
      .replace(/^ {0,3}(?:#{1,6}\s+|>\s?|[-+*]\s+|\d+[.)]\s+)/gm, '')
      .replace(/(\*\*|__|~~|`{1,3}|\*|_)/g, '')
      .replace(/\\([\\`*_[\]{}()#+\-.!>])/g, '$1')
      .replace(/\r\n?/g, '\n')
      .trim();
  }

  private stripMarkdownLinks(text: string): string {
    let plain = '';
    let cursor = 0;
    while (cursor < text.length) {
      const image = text.startsWith('![', cursor);
      let labelStart = -1;
      if (image) {
        labelStart = cursor + 2;
      } else if (text[cursor] === '[') {
        labelStart = cursor + 1;
      }
      if (labelStart < 0) {
        plain += text[cursor++];
        continue;
      }
      const labelEnd = text.indexOf('](', labelStart);
      const urlEnd = labelEnd < 0 ? -1 : text.indexOf(')', labelEnd + 2);
      if (labelEnd < 0 || urlEnd < 0) {
        plain += text[cursor++];
        continue;
      }
      plain += text.slice(labelStart, labelEnd);
      cursor = urlEnd + 1;
    }
    return plain;
  }

  private stripHtml(text: string): string {
    let plain = '';
    let cursor = 0;
    while (cursor < text.length) {
      if (text.startsWith('<!--', cursor)) {
        const commentEnd = text.indexOf('-->', cursor + 4);
        if (commentEnd < 0) {
          break;
        }
        cursor = commentEnd + 3;
        continue;
      }
      if (text[cursor] === '<') {
        const tagEnd = text.indexOf('>', cursor + 1);
        if (tagEnd >= 0) {
          cursor = tagEnd + 1;
          continue;
        }
      }
      plain += text[cursor++];
    }
    return plain;
  }

  private decodeHtmlEntities(text: string): string {
    const namedEntities: Record<string, string> = {
      amp: '&',
      apos: "'",
      gt: '>',
      lt: '<',
      nbsp: ' ',
      quot: '"',
    };
    return text.replace(
      /&(?:#(\d+)|#x([\da-f]+)|([a-z]+));/gi,
      (entity, decimal: string | undefined, hex: string | undefined, named: string | undefined) => {
        if (decimal || hex) {
          const codePoint = Number.parseInt(decimal ?? hex ?? '', decimal ? 10 : 16);
          return Number.isInteger(codePoint) && codePoint >= 0 && codePoint <= 0x10ffff
            ? String.fromCodePoint(codePoint)
            : entity;
        }
        return namedEntities[named?.toLowerCase() ?? ''] ?? entity;
      },
    );
  }

  private escape(text: string): string {
    return text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
  }

  private format(text: string): string {
    return this.escape(text)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replaceAll('\n', '<br>');
  }
}
